import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ChevronRight, Shield, Zap, RotateCcw, Star } from "lucide-react";
import { api } from "@/api";

const HERO_PHONE = "https://images.unsplash.com/photo-1762512949121-c1fc05b36e1a?w=700&q=85&auto=format";
const HERO_PHONE2 = "https://images.unsplash.com/photo-1759588071814-f960ed8f7ee8?w=600&q=85&auto=format";
const HERO_BG = "https://images.unsplash.com/photo-1689871176648-1cf945ff631f?w=1400&q=60&auto=format";

const STATIC_FALLBACK_REVIEWS = [
  { name: "Rahul Sharma", city: "Delhi", rating: 5, text: "Excellent phone condition. Battery health exactly as shown. Delivery was fast and packaging was secure. Highly recommend!" },
  { name: "Neha Verma", city: "Mumbai", rating: 5, text: "Very smooth buying experience. The phone looked almost new. IMEI was clean and everything worked perfectly." },
  { name: "Arjun Singh", city: "Bangalore", rating: 5, text: "Got iPhone 15 Pro for ₹55k. Same condition as described. 50-point inspection report was detailed and honest." },
  { name: "Priya Kapoor", city: "Pune", rating: 5, text: "Sold my old iPhone here — got ₹8000 more than other platforms. Process was smooth, payment in wallet within 24 hrs!" },
  { name: "Vikram Patel", city: "Ahmedabad", rating: 5, text: "Best price for my old iPhone 13. Got instant quote, picked up same day. Very professional service!" },
  { name: "Sneha Reddy", city: "Hyderabad", rating: 5, text: "Ordered iPhone 14 and received it next day. Quality was amazing — just like new. 10/10 experience!" },
];

const TRUST_BADGES = [
  { icon: BadgeCheck, text: "50+ Point Inspection" },
  { icon: Zap, text: "Battery Health Checked" },
  { icon: Shield, text: "IMEI Verified" },
  { icon: RotateCcw, text: "7-Day Return" },
];

const SCROLL_STORY = [
  { pct: "0%", label: "Premium Used iPhones", sub: "Professional grade devices" },
  { pct: "25%", label: "50+ Point Tested", sub: "Every function verified" },
  { pct: "50%", label: "Battery Health Checked", sub: "Know exactly what you get" },
  { pct: "75%", label: "IMEI Verified", sub: "Clean, no-block guarantee" },
  { pct: "100%", label: "Ready For You", sub: "Ship within 24 hours" },
];

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function AnimCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useInView(0.3);
  useEffect(() => {
    if (!visible) return;
    const step = target / (duration / 16);
    let cur = 0;
    const t = setInterval(() => {
      cur = Math.min(cur + step, target);
      setCount(Math.floor(cur));
      if (cur >= target) clearInterval(t);
    }, 16);
    return () => clearInterval(t);
  }, [visible, target, duration]);
  return <span ref={ref}>{count.toLocaleString("en-IN")}{suffix}</span>;
}

export default function PremiumHeroSection({ cartCount, user, mode = "standalone" }) {
  const [liveReviews, setLiveReviews] = useState([]);
  const [storyIdx, setStoryIdx] = useState(0);
  const storyRef = useRef(null);
  const [trustRef, trustVisible] = useInView(0.15);
  const [whyRef, whyVisible] = useInView(0.1);
  const [statsRef, statsVisible] = useInView(0.2);
  const [sellRef, sellVisible] = useInView(0.15);
  const [reviewRef, reviewVisible] = useInView(0.1);

  // Fetch live reviews from DB
  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await api.get("/api/reviews?limit=20");
      if (data && data.length > 0) setLiveReviews(data);
    } catch {
      // fallback to static if API fails
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Use live reviews if available, fallback to static (need at least 1)
  const reviews = liveReviews.length >= 1 ? liveReviews : STATIC_FALLBACK_REVIEWS;

  useEffect(() => {
    const el = storyRef.current;
    if (!el) return;
    const onScroll = () => {
      const { top, height } = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (-top) / (height - window.innerHeight)));
      setStoryIdx(Math.min(SCROLL_STORY.length - 1, Math.floor(pct * SCROLL_STORY.length)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════ */}
      {mode === "standalone" && (
      <section className="phi-hero" data-testid="premium-hero">
        <div className="phi-hero-bg">
          <img src={HERO_BG} alt="" aria-hidden="true" />
          <div className="phi-hero-overlay" />
          <div className="phi-grid-lines" aria-hidden="true" />
        </div>

        <div className="phi-hero-content">
          <div className="phi-hero-text">
            <span className="phi-eyebrow">
              <span className="phi-eyebrow-dot" />
              Verified · Tested · Trusted
            </span>
            <h1 className="phi-hero-h1">
              Used iPhones.<br />
              <span className="phi-gradient-text">Premium Experience.</span>
            </h1>
            <p className="phi-hero-sub">Professionally tested iPhones at prices you'll love. Every device certified.</p>

            <div className="phi-hero-badges">
              {TRUST_BADGES.map(({ icon: Icon, text }) => (
                <span key={text} className="phi-trust-badge">
                  <Icon size={12} /> {text}
                </span>
              ))}
            </div>

            <div className="phi-hero-ctas">
              <Link to="/category" className="phi-btn-primary" data-testid="hero-shop-btn">
                Shop iPhones <ChevronRight size={16} />
              </Link>
              <Link to="/sell" className="phi-btn-outline" data-testid="hero-sell-btn">
                Sell Your iPhone
              </Link>
            </div>
          </div>

          <div className="phi-hero-visual">
            <div className="phi-phone-stage">
              <div className="phi-phone-glow" />
              <div className="phi-phone-ring phi-ring-1" />
              <div className="phi-phone-ring phi-ring-2" />
              <img src={HERO_PHONE} alt="Premium iPhone" className="phi-hero-phone phi-phone-main" />
              <img src={HERO_PHONE2} alt="iPhone" className="phi-hero-phone phi-phone-secondary" />
              <div className="phi-hero-particles">
                {[...Array(8)].map((_, i) => <span key={i} className={`phi-particle phi-p${i}`} />)}
              </div>
            </div>

            <div className="phi-hero-stats-row">
              <div className="phi-hero-stat"><strong>10K+</strong><span>Phones Sold</span></div>
              <div className="phi-hero-stat-sep" />
              <div className="phi-hero-stat"><strong>98%</strong><span>Satisfaction</span></div>
              <div className="phi-hero-stat-sep" />
              <div className="phi-hero-stat"><strong>50+</strong><span>QC Points</span></div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ══ TRUST BAR ══════════════════════════════════════════════ */}
      <section className={`phi-trust-bar${trustVisible ? " visible" : ""}`} ref={trustRef} data-testid="trust-bar">
        <div className="phi-trust-bar-inner">
          {[
            ["🔍", "50+ Point Inspection", "Every function tested"],
            ["🔋", "Battery Health Verified", "Exact % shown on listing"],
            ["📋", "IMEI Verified", "Clean & carrier-unlocked"],
            ["↩", "7-Day Returns", "No questions asked"],
            ["🛡", "Warranty Available", "On select devices"],
            ["🚀", "Fast Shipping", "24-hour dispatch"],
          ].map(([icon, title, sub], i) => (
            <div className="phi-trust-item" key={title} style={{ animationDelay: `${i * 0.08}s` }}>
              <span className="phi-trust-icon">{icon}</span>
              <div><b>{title}</b><small>{sub}</small></div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ WHY BUY FROM US ════════════════════════════════════════ */}
      <section className={`phi-why${whyVisible ? " visible" : ""}`} ref={whyRef} id="why-us" data-testid="why-buy-section">
        <div className="phi-section-head">
          <span className="phi-section-eyebrow">OUR PROMISE</span>
          <h2>Why Buy From DMMobile Shop?</h2>
          <p>Every device goes through a rigorous process before reaching you.</p>
        </div>
        <div className="phi-why-grid">
          {[
            { icon: "🔬", title: "50+ Point Inspection", desc: "Screen, battery, cameras, speakers, ports — everything tested thoroughly.", delay: 0 },
            { icon: "🔋", title: "Battery Health Badge", desc: "Exact battery percentage shown on every listing. No surprises.", delay: 0.08 },
            { icon: "📋", title: "IMEI Verified", desc: "Clean IMEI, no blacklist, no block. Carrier unlocked guarantee.", delay: 0.16 },
            { icon: "🔧", title: "Original Parts Check", desc: "Transparent device history and part authenticity disclosed.", delay: 0.24 },
            { icon: "↩", title: "7-Day Return Policy", desc: "Something not right? Return it within 7 days, no questions asked.", delay: 0.32 },
            { icon: "🛡", title: "Warranty Available", desc: "Extended warranty available on eligible certified devices.", delay: 0.4 },
          ].map(({ icon, title, desc, delay }) => (
            <div className="phi-why-card" key={title} style={{ animationDelay: `${delay}s` }}>
              <span className="phi-why-icon">{icon}</span>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ SCROLL STORY ═══════════════════════════════════════════ */}
      <section className="phi-story-wrap" ref={storyRef} data-testid="scroll-story">
        <div className="phi-story-sticky">
          <div className="phi-story-left">
            <div className="phi-story-phone-wrap">
              <div className="phi-story-glow" />
              <img src={HERO_PHONE} alt="iPhone inspection" className="phi-story-phone" />
              <div className="phi-story-lines">
                {SCROLL_STORY.map((s, i) => (
                  <div key={s.label} className={`phi-story-line${i <= storyIdx ? " active" : ""}`} style={{ top: `${15 + i * 16}%` }}>
                    <div className="phi-story-dot" />
                    <div className="phi-story-connector" />
                    <div className={`phi-story-label${i === storyIdx ? " current" : ""}`}>
                      <b>{s.label}</b>
                      <small>{s.sub}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="phi-story-right">
            <span className="phi-section-eyebrow">SEE THE DIFFERENCE</span>
            <h2>See the Difference<br />Before You Buy.</h2>
            <div className="phi-story-steps">
              {SCROLL_STORY.map((s, i) => (
                <div key={s.label} className={`phi-story-step${i <= storyIdx ? " done" : ""}${i === storyIdx ? " active" : ""}`}>
                  <div className="phi-story-step-icon">{i < storyIdx ? "✓" : i + 1}</div>
                  <div><b>{s.label}</b><small>{s.sub}</small></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ SELL YOUR iPHONE ═══════════════════════════════════════ */}
      <section className={`phi-sell${sellVisible ? " visible" : ""}`} ref={sellRef} id="sell" data-testid="sell-section">
        <div className="phi-sell-inner">
          <div className="phi-sell-content">
            <span className="phi-section-eyebrow">SELL YOUR iPHONE</span>
            <h2>Got an iPhone<br />to Sell?</h2>
            <p>Get the best value for your used iPhone. Instant quote, fast payment.</p>
            <div className="phi-sell-steps">
              {["Select your iPhone", "Tell us its condition", "Get your estimated value"].map((step, i) => (
                <div className="phi-sell-step" key={step}>
                  <div className="phi-sell-step-num">{i + 1}</div>
                  {i < 2 && <div className="phi-sell-step-line" />}
                  <span>{step}</span>
                </div>
              ))}
            </div>
            <Link to="/sell" className="phi-btn-primary" data-testid="sell-cta-btn">
              Get Instant Quote →
            </Link>
          </div>
          <div className="phi-sell-visual">
            <div className="phi-sell-card">
              <img src="https://images.unsplash.com/photo-1709178294947-42963a9a33b8?w=400&q=80" alt="Sell iPhone" />
              <div className="phi-sell-card-tag">
                <Star size={12} fill="currentColor" /> Best Resale Value
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══════════════════════════════════════════ */}
      <section className={`phi-reviews${reviewVisible ? " visible" : ""}`} ref={reviewRef} data-testid="testimonials-section">
        <div className="phi-section-head">
          <span className="phi-section-eyebrow">CUSTOMER REVIEWS</span>
          <h2>What Our Customers Say</h2>
        </div>
        <div className="phi-reviews-scroll-outer">
          <div className="phi-reviews-track" data-testid="reviews-carousel-track">
            {[...reviews, ...reviews].map((r, idx) => (
              <div className="phi-review-card" key={idx} aria-hidden={idx >= reviews.length}>
                <div className="phi-review-stars">{"★".repeat(r.rating)}</div>
                <p className="phi-review-text">"{r.text}"</p>
                <div className="phi-review-author">
                  <div className="phi-review-avatar">{r.name[0]}</div>
                  <div><b>{r.name}</b><small>{r.city}</small></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ STATS ══════════════════════════════════════════════════ */}
      <section className={`phi-stats${statsVisible ? " visible" : ""}`} ref={statsRef} data-testid="stats-section">
        <div className="phi-stats-grid">
          {[
            { num: 10000, suffix: "+", label: "Phones Sold" },
            { num: 50, suffix: "+", label: "QC Inspection Points" },
            { num: 98, suffix: "%", label: "Customer Satisfaction" },
            { num: 7, suffix: " Days", label: "Easy Returns" },
          ].map(({ num, suffix, label }) => (
            <div className="phi-stat-card" key={label}>
              <strong><AnimCounter target={num} suffix={suffix} /></strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
